import {createSlice} from '@reduxjs/toolkit';

const initialState = {
  currentStep: 1,
  formData: {
    // Setup step data
    name: '',
    rewardId: '',
    isDuplicateFlow: false,
    rewardType: '',
    allCustomers: true,
    segmentExclude: false,
    selectedSegments: [],
    // Bonus products specific fields
    selectedProducts: [],
    fetchedProducts: [],
    orderQty: '',
    limitOfProductQty: '',
    alertDisplayCount: '', // For BONUS_PRODUCTS reward type (global, not per-product) - must be > 0 to proceed
    // Attributes step fields
    startDate: null,
    endDate: null,
    startDateDisplay: '',
    endDateDisplay: '',
    displayOutOfStock: false,
    inStock: false,
    unlimitedInventory: false,
    // Cash/Physical/Product/visacard specific fields
    quota: '',
    leftOverInventory: '',
    limitPerStore: '',
    maxQtyPerOrder: '',
    routeTypeId: '',
    //pepvoucherorproducts fields
    selectedPepVouchersProducts: [],
    // Visa card type both fields (physical and digital)
    quotaDigitalCard: '',
    leftOverInventoryDigitalCard: '',
    limitPerStoreDigitalCard: '',
    quotaPhysicalCard: '',
    leftOverInventoryPhysicalCard: '',
    limitPerStorePhysicalCard: '',
    // PEP vouchers or products
    selectedPepVouchersOrProducts: [],
    // Display step fields (per product only)
    display: {
      // productId -> { useDefaultImage:boolean, images:[{position,url,file}], defaultImages:[url], alertDisplayCount:number (non-BONUS_PRODUCTS only), rewardText: { lang: {title, description} } }
      // Note: showOnHomePage moved to formData level for all reward types
      // Note: alertDisplayCount moved to formData level for BONUS_PRODUCTS only (see alertDisplayCount field above)
      productDisplay: {}
    },
    showOnHomePage: false, // Reward-level toggle (moved from per-product)
    // Visa cards related properties
    redeemPoints: '',
    cardType: '',
    rewardCashAmount: '',
    // Cash/Discount/PepVoucher Discount related properties
    value: ''
  },
  validation: {
    nameValid: undefined,
    rewardTypeValid: undefined,
    selectedProductsValid: undefined,
    orderQtyValid: undefined,
    limitOfProductQtyValid: undefined,
    // Visa cards
    redeemPointsValid: undefined,
    cardTypeValid: undefined,
    rewardCashAmountValid: undefined,
    // Cash/Discount/PepVoucher Discount
    valueValid: undefined,
    // Attributes step validation
    startDateValid: undefined,
    endDateValid: undefined,
    // Cash/Physical/Product/VisaCard(Physical or Digital Card Type) validation
    quotaValid: undefined,
    leftOverInventoryValid: undefined,
    limitPerStoreValid: undefined,
    maxQtyPerOrderValid: undefined,
    // Visa card type both fields (physical and digital)
    quotaDigitalCardValid: undefined,
    leftOverInventoryDigitalCardValid: undefined,
    limitPerStoreDigitalCardValid: undefined,
    quotaPhysicalCardValid: undefined,
    leftOverInventoryPhysicalCardValid: undefined,
    limitPerStorePhysicalCardValid: undefined
  },
  isLoading: false,
  error: null
};

const rewardsSlice = createSlice({
  name: 'rewards',
  initialState,
  reducers: {
    setCurrentStep: (state, action) => {
      const nextStep = Number(action.payload);
      if (Number.isFinite(nextStep)) {
        state.currentStep = nextStep;
      }
    },
    setFormData: (state, action) => {
      state.formData = {...state.formData, ...action.payload};
    },
    setValidation: (state, action) => {
      state.validation = {...state.validation, ...action.payload};
    },
    resetRewardsData: (state, action) => {
      const defaultData = action.payload || {
        currentStep: 1,
        formData: {
          name: '',
          rewardId: '',
          isDuplicateFlow: false,
          rewardType: '',
          allCustomers: true,
          segmentExclude: false,
          selectedSegments: [],
          selectedProducts: [],
          fetchedProducts: [],
          orderQty: '',
          limitOfProductQty: '',
          alertDisplayCount: '', // For BONUS_PRODUCTS reward type (global, not per-product) - must be > 0 to proceed
          display: {
            productDisplay: {}
          },
          showOnHomePage: false,
          redeemPoints: '',
          cardType: '',
          rewardCashAmount: '',
          // Cash/Discount/PepVoucher Discount related properties
          value: '',
          // Attributes step fields
          startDate: null,
          endDate: null,
          startDateDisplay: '',
          endDateDisplay: '',
          displayOutOfStock: false,
          inStock: false,
          unlimitedInventory: false,
          // Cash/Physical/Product specific fields
          quota: '',
          leftOverInventory: '',
          limitPerStore: '',
          maxQtyPerOrder: '',
          routeTypeId: '',
          // PEP vouchers or products
          selectedPepVouchersProducts: [],
          selectedPepVouchersOrProducts: [],
          // Visa card type both fields (physical and digital)
          quotaDigitalCard: '',
          leftOverInventoryDigitalCard: '',
          limitPerStoreDigitalCard: '',
          quotaPhysicalCard: '',
          leftOverInventoryPhysicalCard: '',
          limitPerStorePhysicalCard: ''
        },

        validation: {
          nameValid: undefined,
          rewardTypeValid: undefined,
          selectedProductsValid: undefined,
          orderQtyValid: undefined,
          limitOfProductQtyValid: undefined,
          redeemPointsValid: undefined,
          cardTypeValid: undefined,
          rewardCashAmountValid: undefined,
          // Cash/Discount/PepVoucher Discount
          valueValid: undefined,
          // Attributes step validation
          startDateValid: undefined,
          endDateValid: undefined,
          // Cash/Physical/Product/VisaCard(Physical or Digital Card Type) validation
          quotaValid: undefined,
          leftOverInventoryValid: undefined,
          limitPerStoreValid: undefined,
          maxQtyPerOrderValid: undefined,
          // Visa card type both fields (physical and digital)
          quotaDigitalCardValid: undefined,
          leftOverInventoryDigitalCardValid: undefined,
          limitPerStoreDigitalCardValid: undefined,
          quotaPhysicalCardValid: undefined,
          leftOverInventoryPhysicalCardValid: undefined,
          limitPerStorePhysicalCardValid: undefined,
          // PEP vouchers or products validation - Add this field
          selectedPepVouchersProductsValid: undefined
        }
      };
      return {...state, ...defaultData};
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    }
  }
});

export const {
  setCurrentStep,
  setFormData,
  setValidation,
  resetRewardsData,
  setLoading,
  setError
} = rewardsSlice.actions;

export default rewardsSlice.reducer;
